import React from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { formatAdsbLastDoneHoursDisplay } from '../../utils/adsbLastPerformedDisplay';

/**
 * Last performed inputs for one AD/SB schedule row (shared with Recurrence "Last performed at" section and live table).
 * @param {Object} props
 * @param {Object} props.row - form schedule row
 * @param {number} props.scheduleIndex
 * @param {boolean} props.readOnly
 * @param {'grid'|'compact'} props.variant
 * @param {(index: number, field: string, value: unknown) => void} props.onScheduleChange
 */
const ScheduleRowLastPerformedFields = ({ row, scheduleIndex, readOnly, variant, onScheduleChange }) => {
  const ro = { readOnly };
  const idx = scheduleIndex;
  const ch = onScheduleChange;

  if (!row.recurrenceBasedOn) return null;

  const inner =
    row.recurrenceBasedOn === 'HOURS' ? (
      <>
        {variant === 'grid' ? (
          <>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Last performed at (hours)"
                type="number"
                value={row.lastDoneValue === '' || row.lastDoneValue == null ? '' : (Number.isNaN(Number(row.lastDoneValue)) ? '' : Math.floor(Number(row.lastDoneValue)))}
                onChange={(e) => {
                  const v = e.target.value;
                  const currentMinutes =
                    row.lastDoneValue != null && row.lastDoneValue !== '' && !Number.isNaN(Number(row.lastDoneValue))
                      ? Math.min(59, Math.max(0, Math.round((Number(row.lastDoneValue) % 1) * 60)))
                      : 0;
                  if (v === '') {
                    ch(idx, 'lastDoneValue', currentMinutes === 0 ? '' : currentMinutes / 60);
                    return;
                  }
                  const num = parseInt(v, 10);
                  if (!Number.isNaN(num) && num >= 0) ch(idx, 'lastDoneValue', num + currentMinutes / 60);
                }}
                inputProps={{ min: 0, step: 1 }}
                InputProps={ro}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Minutes"
                type="number"
                value={row.lastDoneValue == null || row.lastDoneValue === '' ? '' : Math.min(59, Math.max(0, Math.round((Number(row.lastDoneValue) % 1) * 60)))}
                onChange={(e) => {
                  const v = e.target.value;
                  const currentHours = row.lastDoneValue != null && row.lastDoneValue !== '' ? Math.floor(Number(row.lastDoneValue)) : 0;
                  if (v === '') {
                    ch(idx, 'lastDoneValue', currentHours === 0 ? '' : currentHours);
                    return;
                  }
                  const num = parseInt(v, 10);
                  if (!Number.isNaN(num) && num >= 0 && num <= 59) ch(idx, 'lastDoneValue', currentHours + num / 60);
                }}
                inputProps={{ min: 0, max: 59 }}
                InputProps={ro}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary">{formatAdsbLastDoneHoursDisplay(row.lastDoneValue) || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Note (optional)"
                value={row.lastDoneNote == null ? '' : row.lastDoneNote}
                onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)}
                placeholder="Optional note"
                InputProps={ro}
              />
            </Grid>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 280 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Hrs"
                type="number"
                sx={{ flex: 1 }}
                value={row.lastDoneValue === '' || row.lastDoneValue == null ? '' : (Number.isNaN(Number(row.lastDoneValue)) ? '' : Math.floor(Number(row.lastDoneValue)))}
                onChange={(e) => {
                  const v = e.target.value;
                  const currentMinutes =
                    row.lastDoneValue != null && row.lastDoneValue !== '' && !Number.isNaN(Number(row.lastDoneValue))
                      ? Math.min(59, Math.max(0, Math.round((Number(row.lastDoneValue) % 1) * 60)))
                      : 0;
                  if (v === '') {
                    ch(idx, 'lastDoneValue', currentMinutes === 0 ? '' : currentMinutes / 60);
                    return;
                  }
                  const num = parseInt(v, 10);
                  if (!Number.isNaN(num) && num >= 0) ch(idx, 'lastDoneValue', num + currentMinutes / 60);
                }}
                inputProps={{ min: 0, step: 1 }}
                InputProps={ro}
              />
              <TextField
                size="small"
                label="Min"
                type="number"
                sx={{ width: 72 }}
                value={row.lastDoneValue == null || row.lastDoneValue === '' ? '' : Math.min(59, Math.max(0, Math.round((Number(row.lastDoneValue) % 1) * 60)))}
                onChange={(e) => {
                  const v = e.target.value;
                  const currentHours = row.lastDoneValue != null && row.lastDoneValue !== '' ? Math.floor(Number(row.lastDoneValue)) : 0;
                  if (v === '') {
                    ch(idx, 'lastDoneValue', currentHours === 0 ? '' : currentHours);
                    return;
                  }
                  const num = parseInt(v, 10);
                  if (!Number.isNaN(num) && num >= 0 && num <= 59) ch(idx, 'lastDoneValue', currentHours + num / 60);
                }}
                inputProps={{ min: 0, max: 59 }}
                InputProps={ro}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">{formatAdsbLastDoneHoursDisplay(row.lastDoneValue) || '—'}</Typography>
            <TextField
              size="small"
              label="Note"
              value={row.lastDoneNote == null ? '' : row.lastDoneNote}
              onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)}
              InputProps={ro}
            />
          </Box>
        )}
      </>
    ) : row.recurrenceBasedOn === 'CYCLES' ? (
      variant === 'grid' ? (
        <>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Last performed at (cycles)"
              value={row.lastDoneValue === '' ? '' : row.lastDoneValue}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  ch(idx, 'lastDoneValue', '');
                  return;
                }
                const allowed = raw.replace(/[^0-9.]/g, '');
                const parts = allowed.split('.');
                const intPart = (parts[0] || '').slice(0, 8);
                const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                ch(idx, 'lastDoneValue', decPart ? `${intPart}.${decPart}` : intPart);
              }}
              InputProps={ro}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Note (optional)"
              value={row.lastDoneNote == null ? '' : row.lastDoneNote}
              onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)}
              placeholder="Optional note"
              InputProps={ro}
            />
          </Grid>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 280 }}>
          <TextField
            size="small"
            label="Cycles"
            value={row.lastDoneValue === '' ? '' : row.lastDoneValue}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                ch(idx, 'lastDoneValue', '');
                return;
              }
              const allowed = raw.replace(/[^0-9.]/g, '');
              const parts = allowed.split('.');
              const intPart = (parts[0] || '').slice(0, 8);
              const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
              ch(idx, 'lastDoneValue', decPart ? `${intPart}.${decPart}` : intPart);
            }}
            InputProps={ro}
          />
          <TextField size="small" label="Note" value={row.lastDoneNote == null ? '' : row.lastDoneNote} onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)} InputProps={ro} />
        </Box>
      )
    ) : row.recurrenceBasedOn === 'LANDINGS' ? (
      variant === 'grid' ? (
        <>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Last performed at (landings)"
              type="number"
              value={row.lastDoneValue === '' || row.lastDoneValue == null ? '' : Math.floor(Number(row.lastDoneValue))}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  ch(idx, 'lastDoneValue', '');
                  return;
                }
                const num = parseInt(v, 10);
                if (!Number.isNaN(num) && num >= 0) ch(idx, 'lastDoneValue', num);
              }}
              inputProps={{ min: 0 }}
              InputProps={ro}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Note (optional)"
              value={row.lastDoneNote == null ? '' : row.lastDoneNote}
              onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)}
              placeholder="Optional note"
              InputProps={ro}
            />
          </Grid>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 280 }}>
          <TextField
            size="small"
            label="Landings"
            type="number"
            value={row.lastDoneValue === '' || row.lastDoneValue == null ? '' : Math.floor(Number(row.lastDoneValue))}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') {
                ch(idx, 'lastDoneValue', '');
                return;
              }
              const num = parseInt(v, 10);
              if (!Number.isNaN(num) && num >= 0) ch(idx, 'lastDoneValue', num);
            }}
            inputProps={{ min: 0 }}
            InputProps={ro}
          />
          <TextField size="small" label="Note" value={row.lastDoneNote == null ? '' : row.lastDoneNote} onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)} InputProps={ro} />
        </Box>
      )
    ) : ['DAYS', 'MONTHS', 'YEARS'].includes(row.recurrenceBasedOn) ? (
      variant === 'grid' ? (
        <>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Last performed at (date)"
              type="date"
              value={row.lastDoneValue == null || row.lastDoneValue === '' ? '' : (typeof row.lastDoneValue === 'string' && row.lastDoneValue.length >= 10 ? row.lastDoneValue.slice(0, 10) : row.lastDoneValue)}
              onChange={(e) => ch(idx, 'lastDoneValue', e.target.value || '')}
              InputLabelProps={{ shrink: true }}
              InputProps={ro}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Note (optional)"
              value={row.lastDoneNote == null ? '' : row.lastDoneNote}
              onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)}
              placeholder="Optional note"
              InputProps={ro}
            />
          </Grid>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 280 }}>
          <TextField
            size="small"
            label="Date"
            type="date"
            value={row.lastDoneValue == null || row.lastDoneValue === '' ? '' : (typeof row.lastDoneValue === 'string' && row.lastDoneValue.length >= 10 ? row.lastDoneValue.slice(0, 10) : row.lastDoneValue)}
            onChange={(e) => ch(idx, 'lastDoneValue', e.target.value || '')}
            InputLabelProps={{ shrink: true }}
            InputProps={ro}
          />
          <TextField size="small" label="Note" value={row.lastDoneNote == null ? '' : row.lastDoneNote} onChange={(e) => ch(idx, 'lastDoneNote', e.target.value)} InputProps={ro} />
        </Box>
      )
    ) : null;

  if (variant === 'compact') {
    return inner;
  }

  return (
    <>
      <Grid item xs={12} md={2}>
        <Typography variant="body2" color="text.secondary">
          {row.recurrenceBasedOn} {row.matchType === 'BETWEEN' ? `${row.recurrenceValue}–${row.recurrenceValue2}` : row.recurrenceValue}
        </Typography>
      </Grid>
      {inner}
    </>
  );
};

export default ScheduleRowLastPerformedFields;
